import { removeElementDom } from '../../utils/dom-utils';
import { addDatalayerAttrs, initDatalayerElement } from '../tracking/datalayer';
import { isMobile } from '../../utils/device-utils';
import { init as initParselyRender } from '../templating/parsely-render';
import { init as initParselyRenderRailMostViewed } from '../templating/parsely-render-rail-most-viewed';
import { isEntertainmentDomain, isTg24Domain } from '../../utils/aem-utils';
import { commonActions } from '../../global-store/subjects/common';
import { changeUrlAndHistory } from '../../utils/change-url';
import { satelliteTracker } from '../../services/tracking/satellite-tracking-service';
import { hidePrimaryShowLocalWithoutTitle, showPrimaryHideLocal } from '../primary-tag/primary-tag';
import { dynamicTrackingOnURLChange } from '../../services/tracking/parsely-tracking-service';
import { isInjectedHP, isInjectedHPInView } from '../../utils/injected-hp-utils';
import { asyncAdvTopBottom, asyncLatestNews } from '../../entrypoints/async-importer';
import { init as initPathbreakerSlider } from '../pathbreaker/pathbreaker';
import { initLazyContentLoader } from '../../entrypoints/lazy-loader';

import { injectedHomepageState } from './state';
import {
  $hidePlayer,
  $railLiveContainer,
  $railLiveSettings,
  HOOK_FOOTER,
  HOOK_GLOBAL_NAV,
  HOOK_HP_MULTIVIDEO,
  HOOK_HP_TABS,
  HOOK_LOCAL_NAV,
  HOOK_TV_CARD,
  ID_PARSELY_SET_OF_CARDS_HP,
  ID_PARSELY_TOP_NEWS_HP,
  INJECTED_HP_ARTICLE_TITLE,
  INJECTED_HP_TITLE,
  NAV_HEIGHT,
  OBSERVER_ELEMENT,
  SATELLITE_CHANGE_TRACK,
  SELECTOR_FOOTER_LOGO,
  SELECTOR_INJECTED_HOMEPAGE,
  SELECTOR_PARSELY_SET_OF_CARDS,
  SELECTOR_PARSELY_TOP_NEWS,
  SELECTOR_PARSELY_TOP_VIDEOS,
  SELECTOR_PLAYER_LIVE_CONTAINER,
  SELECTOR_RAIL_MOBILE_BOTTOM,
  SELECTOR_RAIL_MOBILE_TOP,
} from './constants';

const $hpInArticleRailLiveContainer =
  document.querySelectorAll(SELECTOR_PLAYER_LIVE_CONTAINER).length > 1
    ? document.querySelectorAll(SELECTOR_PLAYER_LIVE_CONTAINER)[1]
    : null;

/** it's not possible use removeElementDom utility because it checks if the element is a DOM element
 *  and we need to remove HTML elements inside an HTMLElement that it's not already a DOM element.
 *  Also you can move the remove function after the HTMLElement became a DOM element but you need to redo query selections.
 */
const removeElements = ($fromElement, selectors) => {
  selectors.forEach((selector) => {
    const $element = $fromElement.querySelector(selector);
    if ($element) {
      removeElementDom($element);
    }
  });
};

/**
 * Change DTM fot tracking
 * @param {*} param DTM params
 */
const changeDTM = (param) => {
  if (typeof param === 'string') {
    const paramToDTM = param.split('=');
    window.digitalData = JSON.parse(paramToDTM[1]);
  } else {
    window.digitalData = param;
  }
  console.info('IHP Digitaldata', window.digitalData);
  addDatalayerAttrs();
};

/**
 * take html(in text) from url
 */
export const httpGetHomepage = async (theUrl, INJECTED_HP_HEADERS = {}) => {
  const httpParameters = { method: 'GET', headers: INJECTED_HP_HEADERS };
  const response = await fetch(theUrl, httpParameters);
  const htmlString = await response.text();
  return new DOMParser().parseFromString(htmlString, 'text/html');
};

const initParselyInjectedHP = () => {
  if (!isMobile()) {
    if (document.getElementById(ID_PARSELY_TOP_NEWS_HP)) {
      initParselyRender(ID_PARSELY_TOP_NEWS_HP, ID_PARSELY_SET_OF_CARDS_HP);
    }
    const $parselyTopVideosNode = document.querySelector(SELECTOR_PARSELY_TOP_VIDEOS);
    if ($parselyTopVideosNode) {
      initParselyRenderRailMostViewed($parselyTopVideosNode);
    }
  }
};

/**
 * Change IDs of parsely node DOM
 * @param {*} hpHtml
 */
export const manageParselyNodeDoms = ($hpHtmlSelected) => {
  if (!isMobile()) {
    const $parselyTopNews = $hpHtmlSelected.querySelector(SELECTOR_PARSELY_TOP_NEWS);
    const $parselyTopNewsElementToHydrate = $hpHtmlSelected.querySelector(SELECTOR_PARSELY_SET_OF_CARDS);
    if ($parselyTopNews && $parselyTopNewsElementToHydrate) {
      $parselyTopNews.id = ID_PARSELY_TOP_NEWS_HP;
      $parselyTopNewsElementToHydrate.id = ID_PARSELY_SET_OF_CARDS_HP;
      $parselyTopNewsElementToHydrate.setAttribute('id', ID_PARSELY_SET_OF_CARDS_HP);
      $parselyTopNews.setAttribute('id', ID_PARSELY_TOP_NEWS_HP);
    }
  }
};

/**
 * Returns the list of selectors to remove based on device and domain
 */
export const getRemovingElements = () => {
  switch (true) {
    case isMobile() && isEntertainmentDomain():
      return [
        HOOK_LOCAL_NAV,
        HOOK_GLOBAL_NAV,
        HOOK_HP_TABS,
        HOOK_HP_MULTIVIDEO,
        SELECTOR_RAIL_MOBILE_TOP,
        SELECTOR_RAIL_MOBILE_BOTTOM,
      ];
    case isMobile():
      return [
        HOOK_LOCAL_NAV,
        HOOK_GLOBAL_NAV,
        HOOK_HP_TABS,
        HOOK_HP_MULTIVIDEO,
        HOOK_TV_CARD,
        SELECTOR_RAIL_MOBILE_TOP,
        SELECTOR_RAIL_MOBILE_BOTTOM,
      ];
    default:
      return [HOOK_LOCAL_NAV, HOOK_GLOBAL_NAV, HOOK_HP_TABS, HOOK_HP_MULTIVIDEO];
  }
};

export const injectNewFontsFromHP = ($hpHtmlHead) => {
  $hpHtmlHead.querySelectorAll('link[rel="preload"][as="font"]').forEach(link => {
    const clone = link.cloneNode(true);
    // avoid duplicates based on href
    const href = clone.getAttribute('href');
    if (!href || document.head.querySelector(`link[href="${href}"]`)) return;

    document.head.appendChild(clone);
  });

  // Collect and inject all @font-face rules from <style> tag
  let fontCssRules = "";
  $hpHtmlHead.querySelectorAll("style").forEach(styleEl => {
    const css = styleEl.textContent || "";
    const matches = css.match(/@font-face\s*{[\s\S]*?}/gi); // non-greedy, multiline
    if (matches) {
      fontCssRules += matches.join("\n") + "\n";
    }
  });
  if (fontCssRules.trim()) {
    const styleTag = document.createElement("style");
    styleTag.textContent = fontCssRules;
    document.head.appendChild(styleTag);
  }

};

/**
 * Inject HP in Article
 * @param {*} $hpHtmlHead
 */
export const injectHP = ($hpHtmlSelected) => {
  const $footerElement = document.querySelector(HOOK_FOOTER);
  const $hpHtmlWrapper = document.createElement('div');
  const customDividerTemplate = `
    <div class="c-footer__variant">
      <span class="c-footer__variant-label">
        <i class="icon icon--social icon--medium icon--left-aligned-m">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 994 994" class="icon__svg icon__svg--facebook-messenger" fill="currentColor" width="80" height="80"><linearGradient id="aaa" gradientUnits="userSpaceOnUse" x1="497" y1="1003" x2="497" y2="9.013" gradientTransform="matrix(1 0 0 -1 0 1003)"><stop offset="0" stop-color="#00b2ff"/><stop offset="1" stop-color="#006aff"/></linearGradient><path d="M497 0C217 0 0 205.1 0 482.1 0 627 59.4 752.2 156.1 838.7c8.1 7.3 13 17.4 13.4 28.3l2.7 88.4c.9 28.2 30 46.5 55.8 35.2l98.6-43.5c8.4-3.7 17.7-4.4 26.5-2 45.3 12.5 93.6 19.1 143.9 19.1 280 0 497-205.1 497-482.1S777 0 497 0z" fill-rule="evenodd" clip-rule="evenodd" fill="url(#aaa)"/><path d="M198.6 623.1l146-231.6c23.2-36.8 73-46 107.8-19.9l116.1 87.1c10.7 8 25.3 7.9 35.9-.1l156.8-119c20.9-15.9 48.3 9.2 34.2 31.4L649.5 602.5c-23.2 36.8-73 46-107.8 19.9l-116.1-87.1c-10.7-8-25.3-7.9-35.9.1L232.8 654.5c-20.9 15.9-48.3-9.2-34.2-31.4z" fill-rule="evenodd" clip-rule="evenodd" fill="#fff"/></svg>
        </i>
        Le news di Sky Sport su Facebook Messenger:
      </span>
      <a class="c-cta c-cta--primary" href="https://sport.sky.it/messenger">
        <span class="c-cta__text">Iscriviti </span>
        <i class="icon icon--arrow-navigation icon--arrow-link icon--c-primary">
          <svg class="icon__svg icon__svg--arrow-link" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="80" height="80">
            <path d="M67.488 35.963L56.259 25.458c-1.328-1.242-1.335-3.264-.017-4.515 1.319-1.251 3.464-1.258 4.792-.016l17.965 16.807c1.393 1.304 1.323 3.446-.153 4.666L58.518 59.207c-1.407 1.163-3.548 1.031-4.782-.294s-1.094-3.342.312-4.505l14.587-12.06H3.388C1.517 42.348 0 40.918 0 39.155c0-1.763 1.517-3.192 3.388-3.192h64.1z" fill="currentColor" fill-rule="evenodd"></path>
          </svg>
        </i>
      </a>
    </div>
  `;

  // removeElement($footerElement, footerElementsToRemove);
  $footerElement.classList.add('c-footer--default', 'c-footer--hp-divider');
  if (!$footerElement.classList.contains('c-footer--default')) {
    $footerElement.querySelector(SELECTOR_FOOTER_LOGO).insertAdjacentHTML('afterend', customDividerTemplate);
  }
  $hpHtmlWrapper.className = OBSERVER_ELEMENT;
  $hpHtmlWrapper.innerHTML = $hpHtmlSelected.outerHTML;
  $footerElement.insertAdjacentElement('afterend', $hpHtmlWrapper);
};

// In order to launch the scripts imported from HP, we need to extract and re-insert them manually as live script elements
const runScriptsFromHp = ($hpHtmlSelected) => {
  $hpHtmlSelected.querySelectorAll('script').forEach(oldScript => {
    const newScript = document.createElement('script');
    if (oldScript.src) {
      newScript.src = oldScript.src; // external script
    } else {
      newScript.textContent = oldScript.textContent; // inline script
    }
    oldScript.remove(); // cleanup
    $hpHtmlSelected.appendChild(newScript);
  });
};

/**
 * Remove ADV Skin
 */
const removeSkinADV = () => {
  if (!isMobile()) {
    const SKIN = document.querySelector('#adSkinDiv');
    const SKINBACKGROUND = document.querySelector('#adSkinDivBg');
    const SKINOFFSET = document.querySelector('#adSkinDivOffset');
    const SKINTOP = document.querySelector('#adSkinDivTop');
    const SKINLEFT = document.querySelector('#adSkinDivLeft');
    const SKINRIGHT = document.querySelector('#adSkinDivRight');
    const SKINIMAGE = document.querySelector('#adSkinDivImg');
    const ArraySkinADV = [SKIN, SKINBACKGROUND, SKINOFFSET, SKINTOP, SKINLEFT, SKINRIGHT, SKINIMAGE].filter(Boolean);
    ArraySkinADV.forEach((element) => removeElementDom(element));
  }
};

const cleanupHP = () => {
  const $HP_INJECT = document.querySelector(SELECTOR_INJECTED_HOMEPAGE);
  const removingElements = getRemovingElements();
  if ($HP_INJECT && removingElements && removingElements.length) {
    removeElements($HP_INJECT, removingElements);
  }
};

const handleHomepageEnter = ({ hpHtmlSelectedDTM, hpURL, currentPageUrl }) => {
  if (!isInjectedHP()) {
    commonActions.setInjectedHP(true);
    const $hpWrapper = document.querySelector('.j-injected-homepage');
    runScriptsFromHp($hpWrapper);
  }
  commonActions.setInjectedHPInView(true);
  if (INJECTED_HP_TITLE) {
    document.title = INJECTED_HP_TITLE;
  }
  // Update DTM, URL, and tracking for homepage
  changeDTM(hpHtmlSelectedDTM);
  changeUrlAndHistory(hpURL);
  satelliteTracker.trackInWeb(SATELLITE_CHANGE_TRACK);
  dynamicTrackingOnURLChange(hpURL, currentPageUrl);
  hidePrimaryShowLocalWithoutTitle();
  // Hide sticky video if present
  if ($hidePlayer && injectedHomepageState.getState('stickyVideoToClick')) {
    $hidePlayer.click();
    injectedHomepageState.setState('stickyVideoToClick', false);
  }
  // Remove ADV skin if present
  if (injectedHomepageState.getState('hasSkinInPage')) {
    removeSkinADV();
    injectedHomepageState.setState('hasSkinInPage', false);
  }
  // Initialize homepage ADV only once
  if (injectedHomepageState.getState('isInitHomepageADV')) {
    asyncAdvTopBottom();
    injectedHomepageState.setState('isInitHomepageADV', false);
  }
  // Enable intersection logic for leaving homepage
  if (!injectedHomepageState.getState('enablePageIntersection')) {
    injectedHomepageState.setState('enablePageIntersection', true);
  }
  // Handle special case for TG24 domain: swap live player containers
  if (isTg24Domain()) {
    if ($hpInArticleRailLiveContainer) {
      $railLiveContainer.innerHTML = '';
      $hpInArticleRailLiveContainer.innerHTML = injectedHomepageState.getState('hpInArticleRailLiveSettings') || '';
    }
  }
};

const handleHomepageLeave = (currentPageUrl, hpURL) => {
  commonActions.setInjectedHPInView(false);
  document.title = INJECTED_HP_ARTICLE_TITLE;
  changeDTM(injectedHomepageState.getState('dtmSettings'));
  changeUrlAndHistory(currentPageUrl);
  satelliteTracker.trackInWeb(SATELLITE_CHANGE_TRACK);
  dynamicTrackingOnURLChange(currentPageUrl, hpURL);
  showPrimaryHideLocal();
  // Restore TG24 live player containers
  if (isTg24Domain()) {
    if ($railLiveContainer) {
      $railLiveContainer.innerHTML = $railLiveSettings;
    }
    if ($hpInArticleRailLiveContainer) {
      $hpInArticleRailLiveContainer.innerHTML = '';
    }
  }
};

const callback = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      // When scrolling down and the sentinel enters the viewport
      asyncLatestNews();
      initParselyInjectedHP();
      initPathbreakerSlider();
      initLazyContentLoader();
      cleanupHP();
      initDatalayerElement();
      handleHomepageEnter({
        hpHtmlSelectedDTM: injectedHomepageState.getState('hpHtmlSelectedDTM'),
        hpURL: injectedHomepageState.getState('hpUrl'),
        currentPageUrl: injectedHomepageState.getState('currentPageUrl'),
      });
    } else if (!entry.isIntersecting && isInjectedHPInView()) {
      // When scrolling up and the sentinel is still in the viewport
      handleHomepageLeave(injectedHomepageState.getState('currentPageUrl'), injectedHomepageState.getState('hpUrl'));
    }
  });
};

/**
 * Run observer for HP
 */
export const runObserver = () => {
  const $targetObserver = document.querySelector(SELECTOR_INJECTED_HOMEPAGE);

  const observerOptions = {
    threshold: [0],
    rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`,
  };

  const observer = new IntersectionObserver(callback, observerOptions);
  if ($targetObserver) {
    observer.observe($targetObserver);
  } else {
    console.warn('IHP Run Observe: .j-injected-homepage element not found');
  }
};
